import { useState, useEffect, useMemo, useContext } from "react";
import * as _ from "lodash";
import { AbsCheckBox, Button, COLORS, Modal, ICONS, Icon } from "abs-ui-components";

import "./Documents.scss";

import CustomTable from "../../../common/customTable/CustomTable";
import AbsModal from "../../../common/absModal/AbsModal";
import BrowseDocumentFile from "./BrowseDocumentFile";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { accessAPIForResponse } from "../../../services/api-services/api-implementations";
import { useSelector } from "react-redux";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";

const Documents = ({ reportDetails }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [documentstableData, setDocuemntsTableData] = useState([]);
	const [documentSubmissions, setDocumentSubmissions] = useState([]);
	const [documentNewSubmissions, setDocumentNewSubmissions] = useState([]);
	const [isUpdated, setIsUpdated] = useState(0);
	const [documentHeader, setDocumentHeader] = useState([]);
	const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
	const [confirmSubmit, setconfirmSubmit] = useState(0);
	const [loading, setloading] = useState(false);
	const [selectedTab, setSelectedTab] = useState("document");

	const user = useSelector((state) => state.userInfo.userData);

	const toggleDocumentModal = () => {
		setIsDocumentsModalOpen(!isDocumentsModalOpen);
		if (isDocumentsModalOpen) {
			setSelectedFiles([]);
		}
	};

	useEffect(() => {
		fetchDocumentsData();
	}, [reportDetails, isUpdated]);

	const isChecked = (reportMstId, documentMstId) => {
		return documentSubmissions.some((submission) => submission.reportMstId === reportMstId && submission.documentMstId === documentMstId);
	};

	const handleCheckboxChange = (reportMstId, documentMstId) => {
		const newSubmission = { reportMstId, documentMstId };
		setDocumentNewSubmissions((prevSubmissions) => {
			const isExisting = prevSubmissions.some((submission) => submission.reportMstId === reportMstId && submission.documentMstId === documentMstId);

			if (isExisting) {
				return prevSubmissions.filter((submission) => !(submission.reportMstId === reportMstId && submission.documentMstId === documentMstId));
			} else {
				return [...prevSubmissions, newSubmission];
			}
		});
	};

	useEffect(() => {}, [documentNewSubmissions]);

	const documentsColumns = useMemo(
		() => [
			{
				header: "Document",
				accessorKey: "documentFileName",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 300,
			},
			...(documentHeader?.map((info) => ({
				header: `${info.reportid}`,
				accessorKey: `${info.reportid}`,
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 250,
				Cell: ({ row: { original } }) => (
					<div className="checkbox-container">
						{isChecked(info.reportMstId, original.documentMstId) && (
							<AbsCheckBox id={`${info.reportMstId}-${original.documentMstId}`} checked={isChecked(info.reportMstId, original.documentMstId)} />
						)}
						{!isChecked(info.reportMstId, original.documentMstId) && (
							<AbsCheckBox
								id={`${info.reportMstId}-${original.documentMstId}`}
								handleChange={() => handleCheckboxChange(info.reportMstId, original.documentMstId)}
								disabled={false}
							/>
						)}
					</div>
				),

				Footer: ({ table }) => documentstableData?.length > 0 && <Button onClick={() => handleOpenModal(info.reportMstId)}>Submit</Button>,
			})) || []),
			{
				accessorKey: "action",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				Cell: ({ row: { original } }) =>
					documentstableData.length > 0 && (
						<Button onClick={() => handleDownload(original.documentMstId, original.documentFileName)}>
							<Icon icon={ICONS.DOWNLOAD} />
						</Button>
					),
				Footer: ({ table }) => documentstableData?.length > 0 && <Button onClick={handleDownloadAll}>Download All</Button>,
			},

			...(userHasClaim(ClaimType.DELETE_DOCUMENT)
				? [
						{
							accessorKey: "deleteAction",
							enableSorting: false,
							enableColumnActions: false,
							enableColumnFilters: false,
							Cell: ({ row: { original } }) =>
								documentstableData.length > 0 && (
									<Button
										primaryColor={COLORS.RED.DARK}
										primaryBackgroundColor={COLORS.RED.LIGHT}
										onClick={() => handleDeleteDocument(original.documentMstId)}
									>
										Delete
									</Button>
								),
						},
				  ]
				: []),
		],

		[documentstableData?.length, documentHeader, documentNewSubmissions, documentSubmissions, isUpdated],
		[]
	);

	const fetchDocumentsData = async () => {
		setloading(true);
		await accessAPIForResponse("GetDocumentsListByReportMstId", null, [reportDetails.ReportMstId])
			.then((response) => {
				if (response?.isSuccess) {
					const responseData = response?.data;
					setDocuemntsTableData(responseData?.documents || []);
					setDocumentHeader(responseData.documentReportInfos || []);
					let submissions = responseData?.documents?.flatMap((docInfo) =>
						docInfo.documentAssociationDtls.map((x) => ({
							documentMstId: x.documentMstId,
							reportMstId: x.reportMstId,
							isSubmitted: x.isSubmitted,
						}))
					);
					setDocumentSubmissions(submissions);
					setDocumentNewSubmissions(submissions);
					setloading(false);
				}
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const uploadDocuments = async (files, keysString, isDocumentTab) => {
		const formData = new FormData();
		files.forEach((file) => {
			formData.append("documentFiles", file);
		});
		formData.append("ReportMstId", reportDetails.ReportMstId);
		if (JSON.parse(keysString).length > 0) {
			formData.append("ChildReportMstIds", JSON.parse(keysString));
		} else {
			formData.append("ChildReportMstIds", "0");
		}
		formData.append("isDocumentTab", isDocumentTab);
		setloading(true);
		axiosHttp
			.post(`api/Documents/UploadDocuments`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			})
			.then((response) => {
				emitToasterMsg("success", "Documents", "File(s) Uploaded Successfully");
				setIsUpdated((prev) => {
					return prev + 1;
				});
				setloading(false);
			})
			.catch((error) => {
				console.error("error", "Error Uploading Files:", error);
			});
	};

	const handleUploadClick = (keysString) => {
		const isDocumentTab = selectedTab === "document";
		uploadDocuments(selectedFiles, keysString, isDocumentTab);
		setSelectedFiles([]);
	};

	const handleDownload = (documentMstId, documentName) => {
		setloading(true);
		axiosHttp
			.get(`api/Documents/DownloadDocument?documentMstId=${documentMstId}`, {
				responseType: "blob",
			})
			.then((response) => {
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("Download", documentName);
				document.body.appendChild(link);
				link.click();
				emitToasterMsg("success", "Document Downloaded Successfully");
				setloading(false);
			})
			.catch((error) => {
				console.error("error", "Error Downloading Documents:", error);
			});
	};

	const handleDeleteDocument = async (documentMstId) => {
        setloading(true);
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
            const deleteDocumentResponse = await accessAPIForResponse("deleteDocument", null, [documentMstId]);
            if (deleteDocumentResponse?.isSuccess) {
                emitToasterMsg("success", deleteDocumentResponse.errorMessage);
                await fetchDocumentsData();
            } else {
                emitToasterMsg("error", deleteDocumentResponse.errorMessage);
                setloading(false);
            }
        } catch (error) {
            console.error("Error while deleting document:", error);
            setloading(false);
        }
    };

	const handleDownloadAll = () => {
		setloading(true);
		axiosHttp
			.get(`api/Documents/DownloadAllDocuments?reportMstId=${reportDetails.ReportMstId}`, {
				responseType: "blob",
			})
			.then((response) => {
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("Download", "All_Documents.zip");
				document.body.appendChild(link);
				link.click();
				emitToasterMsg("success", "All Documents Downloaded Successfully");
				setloading(false);
			})
			.catch((error) => {
				console.error("error", "Error Downloading All Documents:", error);
			});
	};

	const handleOpenModal = (reportMstId) => {
		const checked = documentSubmissions.some((elm) => elm.reportMstId === reportMstId);
		if (documentSubmissions.length !== documentNewSubmissions.length || checked) setconfirmSubmit(reportMstId);
	};

	const handleSubmitDocuments = async (reportMstId) => {
		setloading(true);
		const postData = documentNewSubmissions
			.filter((submission) => submission.reportMstId === reportMstId)
			.map((submission) => ({
				reportMstId: submission.reportMstId,
				documentMstId: submission.documentMstId,
			}));

		if (postData.length === 0) {
			emitToasterMsg("error", "No documents to submit");
			return;
		}

		axiosHttp
			.post(`api/Documents/SubmitDocuments`, postData)
			.then(() => {
				emitToasterMsg("success", "Successfully Submitted Supported Documents");
				setconfirmSubmit(0);

				setIsUpdated((x) => {
					return x + 1;
				});
				setloading(false);
			})
			.catch((error) => {
				console.error("error", "Error For Submitting Supported Documents:", error);
			});
	};

	return (
		<section>
			<div className=" mb-4">
				<Button disabled={false} onClick={toggleDocumentModal}>
					Browse Files
				</Button>
			</div>
			<div className="browse-document-container">
				<CustomTable
					columnsData={documentsColumns}
					renderTableData={documentstableData}
					customProps={{
						enableToptoolbar: false,
						handleExportTable: false,
						showExportButton: false,
						enableStickyFooter: true,
						columnResize: true,
						isLoading: loading,
						enableColumnVirtualization: false,
						enableRowVirtualization: false,
						columnOrder: ["documentFileName", " ...documentHeader.map(info => info.reportid)", "action", "deleteAction"],
						columnPinning: {
							left: ["mrt-row-actions", "documentFileName"],
							right: ["action", "deleteAction"],
						},
					}}
				/>
				{isDocumentsModalOpen && (
					<AbsModal isOpen={isDocumentsModalOpen} title="Browse File" onClose={setIsDocumentsModalOpen} close={toggleDocumentModal}>
						<BrowseDocumentFile
							handleUploadClick={handleUploadClick}
							toggleModal={toggleDocumentModal}
							documentHeader={documentHeader}
							toggleCloseModal={toggleDocumentModal}
							selectedFiles={selectedFiles}
							setSelectedFiles={setSelectedFiles}
						></BrowseDocumentFile>
					</AbsModal>
				)}

				<div className="confirmation-modal-container">
					<Modal
						isOpen={confirmSubmit !== 0}
						modalHeader
						headerHeight={"20px"}
						showFooter
						onCloseClickListener={() => {
							setconfirmSubmit(0);
						}}
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="modal-delete-btn"
									onClick={() => handleSubmitDocuments(confirmSubmit)}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Confirm
								</Button>
								<Button
									className="modal-cancel-btn"
									onClick={() => {
										setconfirmSubmit(0);
									}}
								>
									Return to upload documents
								</Button>
							</div>
						}
					>
						<div>
							<h4>
								<strong>Confirmation</strong>
							</h4>
							<hr></hr>
							<p>Please confirm that all the supporting documents for the following are provided</p>
							<ul>
								<li>FC(voyage), TFJ, FC (electrical), FC(boiler), FC(other) are provided.</li>
								<li>Partial reporting CII data.</li>
								<li>Calculations for manual update of CII.</li>
							</ul>

							<hr />
						</div>
					</Modal>
				</div>
			</div>
		</section>
	);
};

export default Documents;
